import useSwr from 'swr'
import { API_URL } from '@/utils/constants'

/// Fetcher for the volume endpoints. Unlike the shared `fetcher`, it throws on
/// a non-2xx status AND on a 2xx body that isn't a valid `SalesVolume`. This is
/// essential: the ow-api coalescing proxy can return an error ENVELOPE
/// (`{"error":true,"message":"coalesced request owner dropped"}`) — with the
/// shared fetcher that JSON parses as a "successful" result, SWR's `error`
/// stays undefined, the chart's error card is skipped, and the render crashes
/// dereferencing `data.buckets`. Validating the shape here routes the failure
/// into SWR's `error` channel so the chart renders its real error state.
async function salesVolumeFetcher(url: string): Promise<SalesVolume> {
	const res = await fetch(url)
	if (!res.ok) throw new Error(`sales volume HTTP ${res.status}`)
	const body = await res.json()
	if (!body || !Array.isArray(body.buckets)) {
		throw new Error('sales volume response missing buckets')
	}
	return body as SalesVolume
}

/// Marketplace tag → label. Mirrors `sale_classifier::Marketplace` in our
/// ord fork (numeric u8 stored, exposed as the JSON key). Sourced from
/// Domo's Dune classification at dune.com/queries/2148334.
export const MARKETPLACE_LABELS: Record<string, string> = {
	'0': 'Unknown',
	'1': 'OrdinalsWallet',
	'2': 'Satflow',
	'3': 'MagicEden',
	'4': 'OrdSwap',
	'5': 'Gamma',
	'6': 'OrdinalsMarketBTC',
	'7': 'OpenOrdex',
	'8': 'OKX',
	'9': 'UniSat',
	'10': 'ord.net',
	'11': 'Ord Dropz',
	'12': 'DotSwap',
	// Client-minted venue for alkane AMM fills in the sales feed (never in API
	// data). Every pool the tape covers is on the SUBFROST AMM factory (4:65522).
	'100': 'SUBFROST'
}

/// Brand-aligned colors. Sourced from each marketplace's own visual identity:
///   - OW          purple (their "OW" logo gradient)
///   - Satflow     green  (Satflow logomark)
///   - OrdSwap     pink   (Ordswap pink-magenta logo background)
///   - MagicEden   fuchsia (magenta-purple, distinct from OrdSwap pink)
///   - Gamma       amber  (gamma.io brand)
///   - OrdMktBTC   cyan
///   - UniSat      yellow (their gold/yellow brand mark)
///   - ord.net     orange (#FF5000, their favicon/brand colour)
export const MARKETPLACE_COLORS: Record<string, string> = {
	'0': '#6b7280', // gray-500     Unknown
	'1': '#8b5cf6', // violet-500   OrdinalsWallet (purple)
	'2': '#10b981', // emerald-500  Satflow (green)
	'3': '#d946ef', // fuchsia-500  MagicEden (different pink)
	'4': '#ec4899', // pink-500     OrdSwap (pink)
	'5': '#f59e0b', // amber-500    Gamma
	'6': '#06b6d4', // cyan-500     OrdinalsMarketBTC
	'7': '#38bdf8', // sky-400      OpenOrdex
	'8': '#e2e8f0', // slate-200    OKX (brand is black; on a dark chart it
	//                                 reads the way OKX inverts its own mark)
	'9': '#eab308', // yellow-500   UniSat
	'10': '#FF5000', // brand orange ord.net
	'11': '#f472b6', // pink-400     Ord Dropz
	'12': '#22d3ee', // cyan-400     DotSwap (AMM)
	'100': '#38bdf8' // sky-400      SUBFROST
}

interface MarketplaceTally {
	count: number
	volume_sats: number
	count_with_price: number
}

export interface SalesVolumeBucket {
	date: string // 'YYYY-MM-DD'
	block_height_first: number
	block_height_last: number
	by_marketplace: Record<string, MarketplaceTally>
	total: MarketplaceTally
}

export interface SalesVolume {
	from_height: number
	to_height: number
	buckets: SalesVolumeBucket[]
}

/// Global cross-marketplace daily volume. Backed by `GET /sales-volume`
/// on turbo (ow-api) which proxies ord's `/r/sales/volume` under a
/// smart-TTL Cache-Control header (~30s edge cache when touching the
/// current tip, hour-long for range queries strictly in the past).
/// One CF cache entry serves every user instead of each browser pinging
/// ord-lb directly.
export default function useSalesVolume(fromHeight?: number, toHeight?: number) {
	const params = new URLSearchParams()
	if (fromHeight !== undefined) params.set('from_height', String(fromHeight))
	if (toHeight !== undefined) params.set('to_height', String(toHeight))
	const qs = params.toString()
	const url = API_URL ? `${API_URL}/sales-volume${qs ? `?${qs}` : ''}` : null

	return useSwr<SalesVolume>(url, salesVolumeFetcher, {
		// Server side already does the cache window via Cache-Control;
		// we still tick a low-frequency refresh so the tab catches new
		// blocks while open. Aligns with the server's ~30s tip TTL.
		refreshInterval: 60_000,
		revalidateOnFocus: false
	})
}

/// Per-collection volume — same `SalesVolume` shape as the global feed
/// but scoped to a single collection. Backed by
/// `GET /collection/{slug}/sales-volume` on turbo. Server resolves the
/// inscription IDs from our DB (no more client→server ID transmission
/// on every request), proxies to ord, caches per-(slug, from, to) at
/// the CF edge. Same smart TTL as the global feed.
///
/// Pass `undefined` for the slug while it's still loading — the hook
/// becomes a no-op until it arrives.
export function useCollectionVolume(
	slug: string | undefined,
	fromHeight?: number,
	toHeight?: number
) {
	const params = new URLSearchParams()
	if (fromHeight !== undefined) params.set('from_height', String(fromHeight))
	if (toHeight !== undefined) params.set('to_height', String(toHeight))
	const qs = params.toString()
	const url =
		API_URL && slug
			? `${API_URL}/collection/${encodeURIComponent(slug)}/sales-volume${qs ? `?${qs}` : ''}`
			: null

	return useSwr<SalesVolume>(url, salesVolumeFetcher, {
		refreshInterval: 60_000,
		revalidateOnFocus: false
	})
}
